import React, { useEffect, useMemo } from 'react';
import { maxValidator, minValidator, required, requiredNonZero } from '~/app/helpers/validation';
import Loader from '~/app/common/Loader';
import useForm, { UseFormOptionFields } from '~/app/modules/form/useForm';
import { ServiceClosePayload, ServiceDetails } from '~/types';
import classes from './style.module.scss';
import useDidMountEffect from '~/app/hooks/useDidMountEffect';
import Details from './Details';
import Footer from './Footer';
import { useStoreActions, useStoreState } from '~/store';
import { getDynamicScheduleAt } from '~/services/helpers';
import { ValidatorsEnum } from '~/app/modules/form';

export type FormProps = {
  onClose(): void
  serviceDetails: ServiceDetails
  onSave?: (id: ServiceDetails['id']) => void
}

export type FormComponent = React.FC<FormProps>

const Form: FormComponent = ({ onClose, serviceDetails, onSave }) => {
  const machine = useStoreState(state => state.machine.data);

  const isHPCodeRequired = useStoreState(state => state.issue.isHPCodeRequired);
  const hasSimplifiedWorkorder = useStoreState(state => state.service?.hasSimplifiedWorkorder ?? false);
  const hasNextServiceSection = useStoreState(state => state.service.hasNextServiceSection);
  const isWorkOrderFormActive = useStoreState(state => state.service.isWorkOrderFormActive);
  const displayCustomer = useStoreState(state => state.issue.displayCustomer);
  const displayProject = useStoreState(state => state.issue.displayProject);

  const getDefaultNextDynamicServiceOccurrence = useMemo(() => {
      return (serviceDetails?.operatingTime ?? 0) + (serviceDetails?.primaryMeterUnit ?? 0)
  }, [serviceDetails?.operatingTime, serviceDetails?.primaryMeterUnit]);

  const getDefaultNextDynamicScheduleAt = useMemo(() => {
      let currentDate = new Date();
      if (serviceDetails?.secondaryMeterUnit) {
          const defaultNextDynamicScheduleAt = getDynamicScheduleAt(currentDate, serviceDetails.secondaryMeterUnit, serviceDetails?.timeUnit);
          return defaultNextDynamicScheduleAt.toString();
      }
      return undefined;
  }, [serviceDetails.secondaryMeterUnit, serviceDetails.timeUnit]);

  const formFields = useMemo(() => {
    const fields: UseFormOptionFields = [];

    if (hasSimplifiedWorkorder) {
      fields.push(['comment', {}]);
      !!machine.isOperatingTimeVisible && fields.push(['operatingTime', isWorkOrderFormActive && !!machine.operatingTimeAllowed ? { validators: [['required', required]] } : {}]);
      !!machine.isOperatingCountVisible && fields.push(['operatingCount', isWorkOrderFormActive && !!machine.operatingCountAllowed ? { validators: [['required', required]] } : {}]);
      isHPCodeRequired && fields.push(['hpCode', isWorkOrderFormActive ? { validators: [['required', requiredNonZero]] } : {} ]);
      fields.push(['ordinaryHours', isWorkOrderFormActive ? { validators: [['required', required]] } : {}]);
      fields.push(['overTime50', {}]);
      fields.push(['overTime100', {}]);
      fields.push(['workType', isWorkOrderFormActive ? { validators: [['required', requiredNonZero]] } : {}]);
      fields.push(['costCategory', isWorkOrderFormActive ? { validators: [['required', requiredNonZero]] } : {}]);
      fields.push(['customer', isWorkOrderFormActive && displayCustomer ? { validators: [['required', requiredNonZero]] } : {}]);
      fields.push(['project', isWorkOrderFormActive && displayProject ? { validators: [['required', requiredNonZero]] } : {}]);

      if (hasNextServiceSection) {
          if (serviceDetails?.primaryMeterUnit !== null) {
              fields.push(['nextDynamicServiceOccurrence', { validators: [
                  ['required', required],
                  [ValidatorsEnum.min, minValidator(Number(serviceDetails.operatingTime))]
              ] }]);
              fields.push(['nextAlertServiceOccurrence', { validators: [
                  ['required', required],
                  [ValidatorsEnum.min, minValidator(1)],
                  [ValidatorsEnum.max, maxValidator(Number(serviceDetails.primaryMeterUnit))]
              ] }]);
          }

          if (serviceDetails?.scheduleAt !== null) {
              fields.push(['nextDynamicScheduleAt', { validators: [
                  ['required', required]
              ] }]);
  
              fields.push(['nextAlertScheduleDays', { validators: [
                  ['required', required],
                  [ValidatorsEnum.min, minValidator(1)]
              ] }]);
          }
      }
    }

    return fields;
  }, [displayCustomer, displayProject, hasSimplifiedWorkorder, isHPCodeRequired, isWorkOrderFormActive, machine.isOperatingCountVisible, machine.isOperatingTimeVisible, machine.operatingCountAllowed, machine.operatingTimeAllowed, serviceDetails.operatingTime, serviceDetails.primaryMeterUnit, serviceDetails.scheduleAt, hasNextServiceSection]);

  const storeUpdateForm = useStoreActions(actions => actions.form.updateForm);

  useEffect(() => {
    if (hasSimplifiedWorkorder && !isWorkOrderFormActive) {
      const resetState = { validators: [], errors: [], state: { touched: false, invalid: false } };
      storeUpdateForm({
        formName: 'closeService',
        fields: {
          ...(machine.isOperatingTimeVisible && { operatingTime: resetState }),
          ...(machine.isOperatingCountVisible && { operatingCount: resetState }),
          ...(isHPCodeRequired && { hpCode: resetState }),
          ordinaryHours: resetState,
          overTime50: resetState,
          overTime100: resetState,
          workType: resetState,
          costCategory: resetState,
          ...(displayCustomer && { customer: resetState }),
          ...(displayProject && { project: resetState })
        }
      });
    }
  }, [hasSimplifiedWorkorder, isWorkOrderFormActive, storeUpdateForm, machine.isOperatingTimeVisible, machine.isOperatingCountVisible, isHPCodeRequired, displayCustomer, displayProject]);

  const { values, removeForm } = useForm<ServiceClosePayload>({
    formName: 'closeService',
    fields: formFields,
    defaultValues: {
      ...hasSimplifiedWorkorder && {
        comment: '',
        ...machine.isOperatingTimeVisible && { operatingTime: 0 },
        ...machine.isOperatingCountVisible && { operatingCount: 0 },
        nextDynamicServiceOccurrence: getDefaultNextDynamicServiceOccurrence,
        nextDynamicScheduleAt: getDefaultNextDynamicScheduleAt,
        nextAlertServiceOccurrence: serviceDetails.alertServiceOccurrence,
        nextAlertScheduleDays: serviceDetails.alertScheduleDays,
        hpCode: 0,
        workType: 0,
        costCategory: 0,
        customer: 0,
        project: 0,
        ordinaryHours: undefined,
        overTime50: undefined,
        overTime100: undefined
      },
    }
  });

  useDidMountEffect(() => (() => removeForm()));


  if (!values) {
    return <Loader size="32" />;
  }

  return (
    <>
      <div className={classes['container']}>
        <Details />
      </div>

      <Footer onClose={onClose} serviceDetails={serviceDetails} onSave={onSave} />
    </>
  );
};

export default Form;
